import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { useRequest } from 'ahooks';
import type { JsonRpcProvider } from 'ethers';
import type { Connection } from '@solana/web3.js';
import {
	getPositions,
	SKIP_REASON_HINTS,
	SKIP_REASON_LABELS,
	type FeeAuth,
	type Position,
} from '@/api/feeDashboard';
import {
	BalanceState,
	formatAmount,
	positionKey,
	readEvmBalance,
	readSolanaBalance,
} from '@/utils/feeBalances';
import { Card, chainByWormholeId, ChainLogo, Mono, Muted, Pager, Pill, Row, shortHash } from './styles';

type Props = {
	auth: FeeAuth;
	pageSize: number;
	// Read-only providers the interface already keeps per chain, keyed by the
	// EVM chain id (not the wormhole one).
	evmProviders?: Map<number, JsonRpcProvider> | undefined;
	solanaConnection: Connection | null;
};

type Filter = 'all' | 'redeemable' | 'skipped';

const FeePositions: FC<Props> = ({ auth, pageSize, evmProviders, solanaConnection }) => {
	const [filter, setFilter] = useState<Filter>('all');
	const [offset, setOffset] = useState(0);
	const [balances, setBalances] = useState<Record<string, BalanceState>>({});

	const { data, loading } = useRequest(
		async () =>
			getPositions(auth, {
				limit: pageSize,
				offset,
				...(filter === 'all' ? {} : { status: filter }),
			}),
		{ refreshDeps: [auth.signature, filter, offset] },
	);

	const positions = data?.data ?? [];

	// Balances are fetched here, in the browser, from the `read` block the API
	// returns — the backend never touches an RPC for them. One request per
	// position, kept independent so a single failing chain does not blank the
	// whole table.
	const loadBalance = useCallback(
		async (position: Position) => {
			const key = positionKey(position);
			setBalances((prev) => ({ ...prev, [key]: { status: 'loading' } }));
			try {
				let value;
				if (position.read.kind === 'evm') {
					const chain = chainByWormholeId(position.chainId);
					const provider = chain ? evmProviders?.get(chain.chainId) : undefined;
					if (!provider) {
						throw new Error('No RPC configured for this chain');
					}
					value = await readEvmBalance(provider, position);
				} else {
					if (!solanaConnection) {
						throw new Error('No Solana RPC configured');
					}
					value = await readSolanaBalance(solanaConnection, position);
				}
				setBalances((prev) => ({ ...prev, [key]: { status: 'ok', value } }));
			} catch (err: any) {
				setBalances((prev) => ({
					...prev,
					[key]: { status: 'error', message: err?.shortMessage || err?.message || 'Failed to read' },
				}));
			}
		},
		[evmProviders, solanaConnection],
	);

	useEffect(() => {
		for (const position of positions) {
			if (!balances[positionKey(position)]) {
				void loadBalance(position);
			}
		}
		// Intentionally keyed on the rendered page only: re-running on every
		// `balances` change would loop.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [positions, loadBalance]);

	const total = data?.total ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const page = Math.floor(offset / pageSize) + 1;

	const changeFilter = (next: Filter) => {
		setFilter(next);
		setOffset(0);
	};

	return (
		<Box sx={{ width: 1 }}>
			<ToggleButtonGroup
				exclusive
				size="small"
				value={filter}
				onChange={(_, v) => v && changeFilter(v)}
				sx={{ mb: 2 }}
			>
				<ToggleButton value="all">All</ToggleButton>
				<ToggleButton value="redeemable">Auto-redeemed</ToggleButton>
				<ToggleButton value="skipped">Not redeemed</ToggleButton>
			</ToggleButtonGroup>

			{filter === 'skipped' && (
				<Muted variant="appTinyRegular" sx={{ display: 'block', mb: 1.5 }}>
					These balances are safe — they are simply not part of the automatic daily redeem. Each row says
					why.
				</Muted>
			)}

			<Card>
				{loading && positions.length === 0 && (
					<Row>
						<Muted variant="appSmallRegular">Loading balances…</Muted>
					</Row>
				)}
				{!loading && positions.length === 0 && (
					<Row>
						<Muted variant="appSmallRegular">No fee balances found for this wallet.</Muted>
					</Row>
				)}
				{positions.map((position, i) => (
					<PositionRow
						key={positionKey(position)}
						position={position}
						balance={balances[positionKey(position)]}
						divider={i < positions.length - 1}
						onRetry={() => loadBalance(position)}
					/>
				))}
			</Card>

			{total > pageSize && (
				<Pager>
					<Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - pageSize))}>
						Previous
					</Button>
					<Muted variant="appTinyRegular">
						Page {page} of {pageCount} · {total} balances
					</Muted>
					<Button
						size="small"
						disabled={offset + pageSize >= total}
						onClick={() => setOffset(offset + pageSize)}
					>
						Next
					</Button>
				</Pager>
			)}
		</Box>
	);
};

const PositionRow: FC<{
	position: Position;
	balance?: BalanceState;
	divider: boolean;
	onRetry: () => void;
}> = ({ position, balance, divider, onRetry }) => {
	const chain = chainByWormholeId(position.chainId);
	const label = position.tokenKind
		? position.tokenKind.toUpperCase()
		: shortHash(position.token, 6, 4);

	const status = useMemo(() => {
		if (!position.redeemable && position.reason) {
			return {
				tone: 'neutral' as const,
				text: SKIP_REASON_LABELS[position.reason],
				hint: SKIP_REASON_HINTS[position.reason],
			};
		}
		// Eligible by policy — but the daily redeem still skips a balance under
		// the minimum, which is only knowable once the balance is read.
		if (balance?.status === 'ok' && balance.value.aboveMinimum === false) {
			return {
				tone: 'warn' as const,
				text: 'Below minimum',
				hint: position.minAmount
					? `Redeemed automatically once the balance reaches ${position.minAmount.human} ${label}.`
					: undefined,
			};
		}
		return { tone: 'ok' as const, text: 'Auto-redeemed', hint: 'Included in the daily redeem.' };
	}, [position, balance, label]);

	return (
		<Row divider={divider}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
				{chain?.logoURI && <ChainLogo src={chain.logoURI} alt={chain.chainName} />}
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="appSmallRegular" color="common.textPrimary">
						{label}
					</Typography>
					<Muted variant="appTinyRegular" sx={{ display: 'block' }}>
						{chain?.chainName ?? `Chain ${position.chainId}`}
						{position.yourBps != null && position.totalBps
							? ` · your share ${position.yourBps}/${position.totalBps}`
							: ''}
					</Muted>
				</Box>
			</Box>

			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
				<Box sx={{ textAlign: 'right' }}>
					{balance?.status === 'ok' && (
						<Mono variant="appSmallRegular" color="common.textPrimary">
							{formatAmount(balance.value.display)}
						</Mono>
					)}
					{balance?.status === 'loading' && <Muted variant="appTinyRegular">reading…</Muted>}
					{balance?.status === 'error' && (
						<Tooltip title={balance.message}>
							<Muted
								variant="appTinyRegular"
								sx={{ cursor: 'pointer', textDecoration: 'underline' }}
								onClick={onRetry}
							>
								retry
							</Muted>
						</Tooltip>
					)}
					<Box sx={{ mt: 0.5 }}>
						<Tooltip title={status.hint ?? ''}>
							<Pill tone={status.tone}>{status.text}</Pill>
						</Tooltip>
					</Box>
				</Box>
			</Box>
		</Row>
	);
};

export default FeePositions;
